// MARK: - Audio Director (Task 8)
// Port of AudioDirector.generateAudioAssets assembly order from:
// Mr Roast/Mr Roast/Services/Audio/AudioDirector.swift
//
// Skips: Gemini AI call, in-memory cache (handled elsewhere).
// Pure assembly: enhance → cues → music → AudioAssets.

import type { AudioProfile, AudioAssets } from './types';
import type { WordTiming, Mode } from '@/types/roast';
import { makeRng } from './rng';
import { enhance } from './enhance';
import { createSoundEffectCues } from './cues';
import { selectMusicForPersona } from './musicLibrary';

// ---------------------------------------------------------------------------
// MARK: - Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a complete set of audio assets for a roast/toast from an AudioProfile.
 *
 * Assembly order mirrors AudioDirector.generateAudioAssets (Swift):
 *   1. Enhance the profile's sound effect opportunities with fallbacks (enhance).
 *   2. Convert opportunities into timed cues aligned to word timings (createSoundEffectCues).
 *   3. Select a music track for the profile/mode (selectMusicForPersona).
 *   4. Return { musicTrack, cues, profile }.
 *
 * @param profile     AI-generated audio profile (mood, energyLevel, opportunities, tags).
 * @param script      Roast/toast script text (used by enhance to generate fallback cues).
 * @param wordTimings Word-level timings from Gemini TTS forced alignment.
 * @param mode        'roast' or 'toast' (toast forces romantic music).
 * @param seed        Seed for deterministic PRNG — same seed always yields identical assets.
 * @param opts        Forwarded to createSoundEffectCues (see that function's JSDoc).
 *   opts.withinSegmentCursor: pass true on the deck path; omit/false on the photo path.
 */
export function resolveAudioAssets(
  profile: AudioProfile,
  script: string,
  wordTimings: WordTiming[],
  mode: Mode,
  seed: string | number,
  opts?: { withinSegmentCursor?: boolean },
): AudioAssets {
  // 1. Enhance opportunities — pass a fresh seeded rng for determinism
  const opportunities = enhance(profile, script, makeRng(seed));

  // 2. Convert opportunities into timed, spaced, de-duplicated cues
  const cues = createSoundEffectCues(opportunities, wordTimings, makeRng(seed), opts);

  // 3. Select music track (toast mode always overrides to romance)
  const musicTrack = selectMusicForPersona(profile, mode);

  // 4. Assemble and return
  return { musicTrack, cues, profile };
}
